import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Minimize2 } from 'lucide-react';
import { isFullscreen, exitFullscreen, onFullscreenChange } from '../../utils/fullscreen';

/**
 * Tiny escape hatch from the fullscreen a screen enters on selection.
 *
 * Selecting a screen calls requestFullscreen() on the tap gesture (TVs and
 * kiosk tablets have no F11), which left no way back on a touch-only device —
 * Esc needs a keyboard. Renders only while actually fullscreen, sits in the top
 * right, and stays faint until hovered so it never competes with the wall
 * content behind it.
 */
const Button = styled.button`
  position: fixed;
  top: 8px;
  right: 8px;
  z-index: 2147483000; /* above screen content and any overlay */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.35);
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  opacity: 0.25;
  transition: opacity 0.2s ease, background 0.2s ease;
  backdrop-filter: blur(4px);

  &:hover,
  &:focus-visible {
    opacity: 1;
    background: rgba(0, 0, 0, 0.6);
  }

  /* Touch devices get no hover, so keep it findable without being loud. */
  @media (hover: none) {
    opacity: 0.5;
    width: 40px;
    height: 40px;
  }

  @media print {
    display: none;
  }
`;

function ExitFullscreenButton() {
  const [fullscreen, setFullscreen] = useState(isFullscreen());

  useEffect(() => {
    const sync = () => setFullscreen(isFullscreen());
    const off = onFullscreenChange(sync);
    sync();
    return off;
  }, []);

  if (!fullscreen) return null;

  return (
    <Button
      type="button"
      onClick={() => {
        exitFullscreen();
      }}
      title="Exit fullscreen"
      aria-label="Exit fullscreen"
    >
      <Minimize2 size={16} />
    </Button>
  );
}

export default ExitFullscreenButton;
